import { FC, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { EntityLink } from '../entity-link';
import {
  computeIconSize,
  EntityLinkAction,
  EntityLinkSize,
  EntityLinkVariant,
} from '../entity-link/helpers';
import { UserModel } from '@/lib/models/user.model';
import { isUserClaim, UserClaim } from '@/lib/models/user-event';
import useDrawer from '@/hooks/use-drawer.hook';
import UserAvatar from '../avatar/user-avatar';

interface Props {
  user: UserModel | UserClaim;
  showEmail?: boolean;
  showAvatar?: boolean;
  size?: EntityLinkSize;
  variant?: EntityLinkVariant;
  action?: EntityLinkAction;
  condensed?: boolean;
}

const UserLink: FC<Props> = ({
  user,
  showAvatar = true,
  showEmail = true,
  size = 'small',
  variant = 'text',
  action = 'drawer',
  condensed = true,
}) => {
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();

  const { name, email } = user;

  const hasName = !!name;
  const displayName = name || email;
  const canDisplayEmail = showEmail && !!email && hasName;
  const identifier = useMemo(() => name || email, [name, email]);
  const avatarSize = useMemo(() => computeIconSize(size, showEmail), [size, showEmail]);

  // Handle navigation
  const handleClick = useCallback(() => {
    const userId = isUserClaim(user) ? user.spUserId : user.id;

    if (action === 'drawer') {
      openDrawer('end-user', { id: userId });
      return;
    }

    navigate({
      to: '/config/end-users',
      search: { drawer: 'end-user', dp_id: userId },
    });
  }, [action, user, navigate, openDrawer]);

  // Create avatar element
  const AvatarEl = showAvatar ? (
    <UserAvatar identifier={identifier} size={avatarSize} hideTooltip />
  ) : undefined;

  const hasClick = action !== 'none';

  return (
    <EntityLink
      image={AvatarEl}
      title={displayName}
      subtext={canDisplayEmail ? email : undefined}
      onClick={hasClick ? handleClick : undefined}
      size={size}
      variant={variant}
      iconSize={avatarSize}
      condensed={condensed}
    />
  );
};

export default UserLink;
